import { useState } from 'react'
import {
  Box, Button, TextField, Alert, CircularProgress, Stack, Typography, Link,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material'
import { requestManualAction, fetchSuggestions, executeStepAction } from './api.js'
import { manualActionErrorMessage } from './suggestionActionInputs.js'
import { CopyButton } from './CopyButton.jsx'
import { US_STATE_CODES } from './constants/usStates.js'
import ExpandableTextField from './ExpandableTextField.jsx'
import DraggableModal from './DraggableModal.jsx'
import PackagesEditor from './PackagesEditor.jsx'

// "Weight is required" (a single field) is now "every package needs a
// weight" (feature/return-label-multi-package) — no rate call/cost estimate
// on this manual path, so packages are weight-only, unlike SuggestionPanel.jsx's
// own AI-Proposed-Step packages editor.
function isFormSubmittable(form) {
  return Boolean(form.rmaNumber?.trim()) && Boolean(form.shippingAddress1?.trim()) && Boolean(form.shippingCity?.trim())
    && Boolean(form.shippingRegion?.trim()) && Boolean(form.shippingPostalCode?.trim())
    && form.packages?.length > 0 && form.packages.every((p) => p.weight)
}

// A brand-new, always-fresh label creation flow — bypasses ManualActionRow's
// shared rendering entirely, since it needs its own two-stage (create label,
// then add a note) flow rather than one Process button. Otherwise
// self-contained the same way ManualActionRow is: its own trigger Button +
// its own DraggableModal, rendered directly in ManualActionsSidebar.jsx's
// Console/OMS Stack. offsetIndex/onOpenChange come from that sidebar's
// shared multi-instance stagger registry.
export default function CreateReturnLabelModal({ conversationId, onActionCompleted, offsetIndex = 0, onOpenChange }) {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [loaded, setLoaded] = useState(null) // { suggestionId, actionIndex, noteActionIndex, action }
  const [form, setForm] = useState({ rmaNumber: '', shippingAddress1: '', shippingAddress2: '', shippingCity: '', shippingRegion: '', shippingPostalCode: '', packages: [{ weight: '' }] })
  const [processState, setProcessState] = useState(null) // { status: 'processing'|'success'|'failed', result, error }
  const [noteText, setNoteText] = useState('')
  const [noteProcessState, setNoteProcessState] = useState(null) // { status: 'processing'|'success'|'failed', error }

  // Loaded once per open — a fresh grounding every time the modal opens,
  // never reusing stale state from a prior open.
  function load() {
    setLoading(true)
    setLoadError(null)
    setLoaded(null)
    setProcessState(null)
    setNoteProcessState(null)
    requestManualAction(conversationId, 'create_return_shipping_label')
      .then(async (res) => {
        const list = await fetchSuggestions(conversationId)
        const suggestion = list.find((s) => s.id === res.suggestionId)
        const action = suggestion?.proposedActions?.[res.actionIndex]
        if (!suggestion || !action) throw new Error('Could not load the resulting action')
        const noteActionIndex = suggestion.proposedActions.findIndex((a, i) => i > res.actionIndex && a.type === 'create_return_shipping_label_add_note')
        setLoaded({ suggestionId: res.suggestionId, actionIndex: res.actionIndex, noteActionIndex: noteActionIndex >= 0 ? noteActionIndex : null, action })
        const shipTo = action.params?.shipTo ?? {}
        setForm({
          rmaNumber: action.params?.rmaErpOrderId ?? '',
          shippingAddress1: shipTo.shippingAddress1 ?? '',
          shippingAddress2: shipTo.shippingAddress2 ?? '',
          shippingCity: shipTo.shippingCity ?? '',
          shippingRegion: shipTo.shippingRegion ?? '',
          shippingPostalCode: shipTo.shippingPostalCode ?? '',
          // Package 1 defaults to the server's own computed weight
          // (Part 1's computeLabelWeight(), already kit-aware) — the agent
          // may still change it, and can add more packages from here.
          packages: [{ weight: action.params?.weight != null ? String(action.params.weight) : '' }],
        })
      })
      .catch((e) => setLoadError(manualActionErrorMessage(e)))
      .finally(() => setLoading(false))
  }

  function openModal() {
    setExpanded(true)
    onOpenChange?.(true)
    load()
  }

  // Closing (via the trigger button again, the modal's own close button, or
  // Escape) resets every bit of state, same as before — reopening always
  // starts a fresh grounding, never reusing stale state from a prior open.
  function closeModal() {
    setExpanded(false)
    onOpenChange?.(false)
    setLoaded(null)
    setLoadError(null)
    setForm({ rmaNumber: '', shippingAddress1: '', shippingAddress2: '', shippingCity: '', shippingRegion: '', shippingPostalCode: '', packages: [{ weight: '' }] })
    setProcessState(null)
    setNoteText('')
    setNoteProcessState(null)
  }

  function handleToggle() {
    if (expanded) closeModal(); else openModal()
  }

  async function handleProcess() {
    if (!loaded) return
    setProcessState({ status: 'processing', result: null, error: null })
    try {
      const res = await executeStepAction(loaded.suggestionId, loaded.actionIndex, { [loaded.actionIndex]: form }, { source: 'manual_panel' })
      if (res.success) {
        setProcessState({ status: 'success', result: res.result ?? null, error: null })
        setNoteText(`A new prepaid return shipping label was manually created via Other Actions (tracking ${res.result?.trackingNumber ?? ''}) and sent to the buyer.`)
        // Draft update: this is the deliberate exception to manual actions'
        // usual skipDraft: true — the CSR explicitly asked to create a label,
        // so the draft SHOULD reflect the real label URL just resolved.
        onActionCompleted?.('create_return_shipping_label', res.result?.replyText)
      } else {
        setProcessState({ status: 'failed', result: null, error: res.error ?? 'Action failed' })
      }
    } catch (e) {
      setProcessState({ status: 'failed', result: null, error: e.message })
    }
  }

  async function handleProcessNote() {
    if (!loaded?.noteActionIndex && loaded?.noteActionIndex !== 0) return
    setNoteProcessState({ status: 'processing', error: null })
    try {
      const res = await executeStepAction(
        loaded.suggestionId,
        loaded.noteActionIndex,
        { [loaded.noteActionIndex]: { editedNote: noteText, trackingNumber: processState?.result?.trackingNumber, labelUrl: processState?.result?.labelUrl } },
        { source: 'manual_panel' }
      )
      if (res.success) {
        setNoteProcessState({ status: 'success', error: null })
        // Standard manual-action refresh (skipDraft: true) — no forcedDraftText
        // for the note step, only the label-create step above gets that.
        onActionCompleted?.('create_return_shipping_label_add_note')
      } else {
        setNoteProcessState({ status: 'failed', error: res.error ?? 'Action failed' })
      }
    } catch (e) {
      setNoteProcessState({ status: 'failed', error: e.message })
    }
  }

  const created = processState?.status === 'success'
  const rmaNotFound = loaded?.action?.params?.rmaNotFound === true

  return (
    <Box>
      <Button
        fullWidth
        size="small"
        variant="contained"
        color="primary"
        onClick={handleToggle}
        sx={{ justifyContent: 'flex-start' }}
      >
        Create Return Shipping Label
      </Button>
      <DraggableModal
        open={expanded}
        onClose={closeModal}
        title="Create Return Shipping Label"
        centered
        offsetIndex={offsetIndex}
        width={480}
        height={560}
        actions={
          loaded && !created ? (
            <Button
              variant="contained"
              disabled={processState?.status === 'processing' || !isFormSubmittable(form)}
              onClick={handleProcess}
              startIcon={processState?.status === 'processing' ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {processState?.status === 'processing' ? 'Processing…' : 'Process'}
            </Button>
          ) : created && noteProcessState?.status !== 'success' ? (
            <Button
              variant="contained"
              disabled={noteProcessState?.status === 'processing' || !noteText.trim()}
              onClick={handleProcessNote}
              startIcon={noteProcessState?.status === 'processing' ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {noteProcessState?.status === 'processing' ? 'Processing…' : 'Process'}
            </Button>
          ) : null
        }
      >
        {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading…</Typography></Stack>}
        {loadError && <Alert severity="error">{loadError}</Alert>}

        {loaded && !created && (
          <Stack spacing={1.5}>
            {rmaNotFound && (
              <Typography variant="body2" fontWeight={700} color="error">
                No RMA was found on this order — enter an RMA number manually before processing.
              </Typography>
            )}
            <TextField size="small" label="Order Number" disabled value={loaded.action.params?.erpOrderId ?? ''} />
            <TextField
              size="small" label="RMA Number" value={form.rmaNumber}
              onChange={(e) => setForm((f) => ({ ...f, rmaNumber: e.target.value }))}
            />
            <TextField
              size="small" label="Address Line 1" value={form.shippingAddress1}
              onChange={(e) => setForm((f) => ({ ...f, shippingAddress1: e.target.value }))}
            />
            <TextField
              size="small" label="Address Line 2" value={form.shippingAddress2}
              onChange={(e) => setForm((f) => ({ ...f, shippingAddress2: e.target.value }))}
            />
            <TextField
              size="small" label="City" value={form.shippingCity}
              onChange={(e) => setForm((f) => ({ ...f, shippingCity: e.target.value }))}
            />
            <FormControl size="small">
              <InputLabel>State</InputLabel>
              <Select
                label="State"
                value={form.shippingRegion}
                onChange={(e) => setForm((f) => ({ ...f, shippingRegion: e.target.value }))}
              >
                {US_STATE_CODES.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small" label="Postal Code" value={form.shippingPostalCode}
              onChange={(e) => setForm((f) => ({ ...f, shippingPostalCode: e.target.value }))}
            />
            <PackagesEditor
              packages={form.packages}
              onChange={(next) => setForm((f) => ({ ...f, packages: next }))}
            />
            {processState?.status === 'failed' && <Alert severity="error">{processState.error}</Alert>}
          </Stack>
        )}

        {created && (
          <Stack spacing={1.5}>
            {/* Multiple packages: one line per package, each labelled, with its
                own link or its own failure — a package whose label failed to
                create still leaves the others reported, not discarded (see
                action-executor.js's own comment). Falls back to the plain
                single-tracking/labelUrl rendering for a single-package result. */}
            {Array.isArray(processState.result?.labels) ? (
              <Stack spacing={1}>
                {processState.result.labels.map((l) => (
                  <Alert key={l.package} severity={l.error ? 'error' : 'success'}>
                    Package {l.package}{l.error ? `: ${l.error}` : ` — tracking ${l.trackingNumber}`}
                    {!l.error && l.labelUrl && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, minWidth: 0 }}>
                        <Link href={l.labelUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', minWidth: 0, flex: 1 }}>
                          {l.labelUrl}
                        </Link>
                        <CopyButton value={l.labelUrl} />
                      </Stack>
                    )}
                  </Alert>
                ))}
              </Stack>
            ) : (
              <>
                <Alert severity="success">
                  Label created — tracking {processState.result?.trackingNumber}
                </Alert>
                {processState.result?.labelUrl && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Link href={processState.result.labelUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', minWidth: 0, flex: 1 }}>
                      {processState.result.labelUrl}
                    </Link>
                    <CopyButton value={processState.result.labelUrl} />
                  </Stack>
                )}
              </>
            )}
            <Typography variant="body2" fontWeight={700}>Add a note to order {loaded.action.params?.erpOrderId}</Typography>
            <ExpandableTextField
              size="small" label="Order note (editable)" multiline minRows={3} fullWidth
              value={noteText} onChange={(e) => setNoteText(e.target.value)}
              disabled={noteProcessState?.status === 'success'}
            />
            {noteProcessState?.status === 'failed' && <Alert severity="error">{noteProcessState.error}</Alert>}
            {noteProcessState?.status === 'success' && <Alert severity="success">Note added.</Alert>}
          </Stack>
        )}
      </DraggableModal>
    </Box>
  )
}
